export enum Actions {
  /* User exits the flow */
  ABORT = 'abort',
  /* After a user links their policies, we check if their policies & rate report are ready */
  CHECK_POLICIES_AND_RATES = 'check_policies_and_rates',
  /* User either launches the flow for the first time OR just agreed to the NW Canopy Terms of Service */
  CHECK_CONSENT = 'check_consent',
  /* User has a problem fetching rates and wants to try fetching rates again */
  CHECK_RATES = 'check_rates',
  /* Basic "go to next screen" action */
  CONTINUE = 'continue',
  /* User completes or closes the Canopy Connect widget */
  FINISHED_CANOPY_LINKING = 'finished_canopy_linking',
}

export enum Steps {
  CANOPY_WIDGET = 'canopy_widget',
  CLOSED = 'closed',
  CONSENT = 'consent',
  FETCH_POLICIES = 'fetch_policies',
  FETCH_RATES = 'fetch_rates',
  NO_POLICY_DATA_ERROR = 'no_policy_data_error',
  REVIEW_INFORMATION = 'review_information',
  FOUND_POLICIES = 'found_policies',
}

export type PolicyToReview = {
  id: string;
  carrierName: string;
  policyType: string; // TODO: Use `classification` instead
};

export type FlowState = {
  fetchRateAttempts: number;
  hasGivenConsent: boolean;
  hasRateReport?: boolean;
  policy?: PolicyToReview;
  policyNeedsReview?: boolean;
  noPoliciesFound?: boolean;
  pullId?: string;
  step: Steps;
};

type CheckPoliciesAndRatesPayload = {
  hasPolicyData?: boolean;
  hasRateReport?: boolean;
  policyNeedsReview?: boolean;
};

type HasPolicyPayload = CheckPoliciesAndRatesPayload & {
  hasPolicyData: true;
  isFreeRateMember?: boolean;
  isNerdWalletPlusMember: boolean;
  policy: PolicyToReview;
  hasSeenFoundPolicies: boolean;
  noPoliciesFound: boolean;
  pullId: string;
};

type MissingPolicyPayload = CheckPoliciesAndRatesPayload & {
  hasPolicyData?: false;
  isFreeRateMember?: boolean;
  isNerdWalletPlusMember: boolean;
  policy?: never;
  noPoliciesFound?: boolean;
  hasSeenFoundPolicies?: boolean;
  pullId?: string;
};

export type Action =
  | {
      type: Actions.CHECK_CONSENT;
      payload: {
        hasGivenConsent: boolean;
      };
    }
  | {
      type: Actions.CHECK_POLICIES_AND_RATES;
      payload: HasPolicyPayload | MissingPolicyPayload;
    }
  | {
      type: Actions.FINISHED_CANOPY_LINKING;
      payload: {
        pullId: string;
        isNerdWalletPlusMember: boolean;
      };
    }
  | {
      type: Exclude<
        Actions,
        | Actions.FINISHED_CANOPY_LINKING
        | Actions.CHECK_CONSENT
        | Actions.CHECK_POLICIES_AND_RATES
      >;
    };

export const initialFlowState: FlowState = {
  fetchRateAttempts: 0,
  hasGivenConsent: false,
  noPoliciesFound: false,
  step: Steps.CLOSED,
};

const canopyFlowReducer = (state: FlowState, action: Action) => {
  switch (action.type) {
    case Actions.ABORT: {
      return {
        ...state,
        step: Steps.CLOSED,
      };
    }

    case Actions.CHECK_POLICIES_AND_RATES: {
      const { isNerdWalletPlusMember, isFreeRateMember } = action.payload;

      if (isNerdWalletPlusMember || isFreeRateMember) {
        if (state.fetchRateAttempts >= 1) {
          return {
            ...state,
            step: Steps.NO_POLICY_DATA_ERROR,
          };
        }

        if (action.payload.noPoliciesFound) {
          return {
            ...state,
            policy: action.payload.policy,
            step: Steps.CLOSED,
          };
        }

        if (action.payload.policyNeedsReview) {
          return {
            ...state,
            fetchRateAttempts: state.fetchRateAttempts + 1,
            policyNeedsReview: action.payload.policyNeedsReview,
            step: isFreeRateMember ? Steps.CLOSED : Steps.REVIEW_INFORMATION,
          };
        }

        if (action.payload.hasSeenFoundPolicies) {
          return {
            ...state,
            pullId: action.payload.pullId,
            step: Steps.FETCH_POLICIES,
          };
        }

        if (action.payload.hasPolicyData) {
          return {
            ...state,
            policy: action.payload.policy,
            step: Steps.FETCH_RATES,
          };
        }

        if (action.payload.hasRateReport) {
          return {
            ...state,
            hasRateReport: true,
            step: Steps.CLOSED,
          };
        }
      } else {
        if (action.payload.hasPolicyData) {
          return {
            ...state,
            policy: action.payload.policy,
            step: Steps.CLOSED,
          };
        }
      }

      return {
        ...state,
        step: Steps.NO_POLICY_DATA_ERROR,
      };
    }

    case Actions.CHECK_RATES: {
      return {
        ...state,
        fetchRateAttempts: 0,
        step: Steps.FETCH_RATES,
      };
    }

    case Actions.CHECK_CONSENT: {
      return {
        ...state,
        hasGivenConsent: action.payload.hasGivenConsent,
        step: action.payload.hasGivenConsent
          ? Steps.CANOPY_WIDGET
          : Steps.CONSENT,
      };
    }

    case Actions.FINISHED_CANOPY_LINKING: {
      if (action.payload.pullId) {
        return {
          ...state,
          pullId: action.payload.pullId,
          step: action.payload.isNerdWalletPlusMember
            ? Steps.FOUND_POLICIES
            : Steps.FETCH_POLICIES,
        };
      }
      break;
    }

    case Actions.CONTINUE: {
      switch (state.step) {
        case Steps.CLOSED: {
          return {
            ...state,
            step: state.hasGivenConsent ? Steps.CANOPY_WIDGET : Steps.CONSENT,
          };
        }

        case Steps.NO_POLICY_DATA_ERROR: {
          return {
            ...state,
            step: Steps.CLOSED,
          };
        }

        case Steps.REVIEW_INFORMATION: {
          return {
            ...state,
            policyToReview: null,
            step: Steps.FETCH_RATES,
          };
        }

        default: {
          return state;
        }
      }
    }

    default: {
      return state;
    }
  }
};

export default canopyFlowReducer;
